import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { api, get, errMsg } from "@/lib/api";
import { StatusChip } from "@/components/app/StatusChip";
import { Skel } from "@/components/app/Common";
import { ImportSummary } from "@/pages/candidates/import/ImportSummary";

const Stat = ({ label, value, testId, tone = "text-ink" }) => (
  <div data-testid={testId}><p className="text-xs text-ink-muted">{label}</p><p className={`font-display text-2xl font-semibold ${tone}`}>{value}</p></div>
);

function RowActions({ batchId, row, canEdit, refresh }) {
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); refresh(); } catch (e) { toast.error(errMsg(e)); } };
  if (!canEdit) return null;
  if (row.status === "Needs review") {
    const dup = (action, msg) => act(() => api.post(`/candidates/${row.candidate_id}/duplicate`, { action }), msg);
    return (
      <div className="flex gap-1">
        <Button size="sm" variant="outline" className="h-7" onClick={() => dup("merge", "Merged into the existing candidate")} data-testid={`dup-merge-${row.id}`}>Merge</Button>
        <Button size="sm" variant="ghost" className="h-7" onClick={() => dup("keep", "Kept both candidates")} data-testid={`dup-keep-${row.id}`}>Keep both</Button>
        <Button size="sm" variant="ghost" className="h-7 text-ink-muted" onClick={() => dup("skip", "Duplicate skipped")} data-testid={`dup-skip-${row.id}`}>Skip</Button>
      </div>
    );
  }
  if (row.status === "Failed" && row.candidate_id) {
    return (
      <Button size="sm" variant="ghost" className="h-7 text-brand" data-testid={`batch-retry-${row.id}`}
        onClick={() => act(() => api.post(`/batches/${batchId}/items/${row.id}/retry`), "Parsing queued again")}>
        <RotateCcw size={14} /> Retry
      </Button>
    );
  }
  return null;
}

export function BatchTracker({ batchId, canEdit }) {
  const qc = useQueryClient();
  const { data: b } = useQuery({
    queryKey: ["batch", batchId],
    queryFn: () => get(`/batches/${batchId}`),
    refetchInterval: (q) => (q.state.data?.done ? false : 3000),
  });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["batch", batchId] }); qc.invalidateQueries({ queryKey: ["candidates"] }); };
  if (!b) return <Skel className="h-40" />;
  const pct = b.total ? Math.round(((b.total - b.pending) / b.total) * 100) : 0;

  return (
    <section className="panel space-y-4 p-6" data-testid="batch-tracker">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-base font-semibold">Upload progress</h3>
        <StatusChip status={b.done ? "Completed" : "Processing"} testId="batch-status-chip" />
      </div>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-5">
        <Stat label="Total" value={b.total} testId="batch-total" />
        <Stat label="Parsed" value={b.parsed} tone="text-success" testId="batch-parsed" />
        <Stat label="Failed" value={b.failed} tone={b.failed ? "text-risk" : "text-ink"} testId="batch-failed" />
        <Stat label="Duplicates" value={b.duplicates} tone={b.duplicates ? "text-caution" : "text-ink"} testId="batch-duplicates" />
        <Stat label="In progress" value={b.pending} testId="batch-pending" />
      </div>
      <Progress value={pct} className="h-2 bg-brand-tint" data-testid="batch-progress-bar" />
      {b.done && <ImportSummary summary={b.summary} errorsUrl={b.failed ? `/batches/${batchId}/errors.csv` : null} errorsName={`upload-errors-${batchId.slice(0, 8)}.csv`} testId="batch-summary" />}
      <div className="max-h-96 overflow-y-auto rounded-[6px] border border-line">
        <table className="w-full text-sm" data-testid="batch-items-table">
          <tbody className="divide-y divide-line">
            {b.items.map((r) => (
              <tr key={r.id} data-testid={`batch-item-${r.id}`}>
                <td className="px-4 py-2">
                  <p className="truncate text-ink">{r.candidate_name || r.filename}</p>
                  {r.candidate_name && <p className="truncate text-xs text-ink-muted">{r.filename}</p>}
                </td>
                <td className="px-4 py-2"><StatusChip status={r.status} testId={`batch-item-status-${r.id}`} /></td>
                <td className="px-4 py-2 text-xs text-ink-secondary">
                  {r.detail}
                  {r.duplicate_of && <> · <Link to={`/app/candidates/${r.duplicate_of}/resume`} className="text-brand hover:underline">existing</Link></>}
                </td>
                <td className="px-4 py-2 text-right"><RowActions batchId={batchId} row={r} canEdit={canEdit} refresh={refresh} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
